import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { r, s } from '../../theme/tokens';
import { dialogViewportOffset } from './dialogLayoutOffsets';
import type { SxProps, Theme } from './types';
import type { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import type { DialogActionsProps as MuiDialogActionsProps } from '@mui/material/DialogActions';
import type { DialogContentProps as MuiDialogContentProps } from '@mui/material/DialogContent';
import type { DialogTitleProps as MuiDialogTitleProps } from '@mui/material/DialogTitle';

export type DialogProps = MuiDialogProps;
export type DialogActionsProps = MuiDialogActionsProps;
export type DialogContentProps = MuiDialogContentProps;
export type DialogTitleProps = MuiDialogTitleProps;

export const DialogActions = ({ sx, ...rest }: DialogActionsProps): JSX.Element => {
  const baseSx: SxProps<Theme> = { padding: s('md'), gap: s('sm'), justifyContent: 'flex-end' };
  const mergedSx = sx ? ([baseSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>) : baseSx;
  return <MuiDialogActions sx={mergedSx} {...rest} />;
};
export const DialogContent = ({ ...rest }: DialogContentProps): JSX.Element => (
  <MuiDialogContent {...rest} />
);
export const DialogTitle = ({ ...rest }: DialogTitleProps): JSX.Element => (
  <MuiDialogTitle {...rest} />
);

export const Dialog = ({
  children,
  container: containerProp,
  slotProps,
  sx,
  ...rest
}: DialogProps): JSX.Element => {
  const container = containerProp ?? (typeof document === 'undefined' ? undefined : document.body);
  const basePaperSx: SxProps<Theme> = (theme) => ({
    borderRadius: r('lg'),
    padding: s('2xl'),
    backgroundColor: `${theme.app.colors.surface} !important`,
    border: `1px solid ${theme.app.colors.border}`,
    boxSizing: 'border-box',
    backgroundImage: 'none',
    boxShadow: 'none',
    '--Paper-overlay': 'none',
    '--Paper-shadow': 'none',
  });
  const baseBackdropSx: SxProps<Theme> = (theme) => ({
    backgroundColor: alpha(theme.palette.common.black, 0.5),
  });
  const baseRootSx: SxProps<Theme> = (theme) => ({
    '& .MuiDialog-paper': {
      backgroundColor: `${theme.app.colors.surface} !important`,
      backgroundImage: 'none',
      opacity: 1,
    },
    '& .MuiDialogContent-root': {
      backgroundColor: `${theme.app.colors.surface} !important`,
    },
    '& .MuiDialog-container.MuiDialog-scrollPaper': {
      paddingLeft: 'var(--app-dialog-offset, 0px)',
      paddingTop: dialogViewportOffset,
      paddingBottom: dialogViewportOffset,
      alignItems: 'flex-start',
    },
    '& .MuiDialog-container.MuiDialog-scrollBody': {
      paddingLeft: 'var(--app-dialog-offset, 0px)',
      paddingTop: dialogViewportOffset,
      paddingBottom: dialogViewportOffset,
    },
    '& .MuiDialog-container.MuiDialog-scrollBody::after': {
      display: 'none',
    },
    '& .MuiDialog-paper.MuiDialog-paperScrollBody': {
      marginTop: dialogViewportOffset,
      marginBottom: dialogViewportOffset,
      verticalAlign: 'top',
    },
  });
  const paperSlotProps = slotProps?.paper;
  const backdropSlotProps = slotProps?.backdrop;
  const paperSx =
    typeof paperSlotProps === 'function' ? undefined : (paperSlotProps?.sx as SxProps<Theme>);
  const backdropSx =
    typeof backdropSlotProps === 'function' ? undefined : (backdropSlotProps?.sx as SxProps<Theme>);
  const mergedPaperSx = [basePaperSx, paperSx].filter(Boolean) as SxProps<Theme>;
  const mergedBackdropSx = [baseBackdropSx, backdropSx].filter(Boolean) as SxProps<Theme>;
  const resolvedPaperSlotProps =
    typeof paperSlotProps === 'function'
      ? paperSlotProps
      : { ...paperSlotProps, sx: mergedPaperSx };
  const resolvedBackdropSlotProps =
    typeof backdropSlotProps === 'function'
      ? backdropSlotProps
      : { ...backdropSlotProps, sx: mergedBackdropSx };
  const mergedSlotProps = slotProps
    ? { ...slotProps, paper: resolvedPaperSlotProps, backdrop: resolvedBackdropSlotProps }
    : { paper: resolvedPaperSlotProps, backdrop: resolvedBackdropSlotProps };
  const mergedRootSx = sx
    ? ([baseRootSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>)
    : baseRootSx;

  return (
    <MuiDialog {...rest} slotProps={mergedSlotProps} container={container} sx={mergedRootSx}>
      {children}
    </MuiDialog>
  );
};
